import { useState, lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import HubSpotTracker from "@/components/HubSpotTracker";

// Scrolls to in-page anchor (#id) when the URL hash changes or a new route loads with one.
const HashScrollHandler = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    let attempts = 0;
    let cancelled = false;
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempts++ < 80) setTimeout(tryScroll, 100);
    };
    setTimeout(tryScroll, 100);
    return () => { cancelled = true; };
  }, [pathname, hash]);
  return null;
};

// Critical path
import Index from "./pages/Index";

// Lazy-loaded routes
const ScheduleCall = lazy(() => import("./pages/ScheduleCall"));
const Solutions = lazy(() => import("./pages/Solutions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const SmsTerms = lazy(() => import("./pages/SmsTerms"));
const M365Explorer = lazy(() => import("./pages/M365Explorer"));

const ThankYou = lazy(() => import("./pages/ThankYou"));
const CustomerCare = lazy(() => import("./pages/CustomerCare"));
const Migration = lazy(() => import("./pages/Migration"));
const Cloud = lazy(() => import("./pages/Cloud"));
const Managed = lazy(() => import("./pages/Managed"));
const Secure = lazy(() => import("./pages/Secure"));
const ClientSuccess = lazy(() => import("./pages/ClientSuccess"));
const MSP = lazy(() => import("./pages/MSP"));
const NotFound = lazy(() => import("./pages/NotFound"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => {
  const [queryClient] = useState(() => new QueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <HashScrollHandler />
          <HubSpotTracker />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/schedule-a-call" element={<ScheduleCall />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/sms-terms" element={<SmsTerms />} />
              <Route path="/m365-explorer" element={<M365Explorer />} />
              
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/customer-care" element={<CustomerCare />} />
              <Route path="/migration" element={<Migration />} />
              <Route path="/cloud" element={<Cloud />} />
              <Route path="/managed" element={<Managed />} />
              <Route path="/secure" element={<Secure />} />
              <Route path="/m365-success" element={<ClientSuccess />} />
              {/* Legacy redirect for old /client-success URL */}
              <Route path="/client-success" element={<Navigate to="/m365-success" replace />} />
              <Route path="/msp" element={<MSP />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
